import React, { useState, useEffect } from 'react';
import { Spin, Checkbox } from 'antd';
import Card from 'components/Card/Card';
import { useParams } from 'react-router-dom';
import providerService from 'services/providers.service';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from 'containers/Provider/SelectProgram/SelectProgram';
import ProgramInfo from './ApplicationDetails/ProgramInfo';
import StaffMembers from './ApplicationDetails/StaffMembers';
import Residents from './ApplicationDetails/Residents';
import Insurance from './ApplicationDetails/Insurance';
import Accreditations from './ApplicationDetails/Accreditations';
import AirForceHCCAccreditations from './ApplicationDetails/AirForceHCCAccreditations';
import Rates from './ApplicationDetails/Rates';
import RegistrationFee from './ApplicationDetails/RegistrationFee';
import IncludedCosts from './ApplicationDetails/IncludedCosts';
import Discounts from './ApplicationDetails/Discounts';
import FamilyInformation from './ApplicationDetails/FamilyInformation';
import TabularList from 'components/TabularList/TabularList';

const ApplicationDetails = ({ isEditable = false }) => {
  const [loading, setLoading] = useState(null);
  const [application, setApplication] = useState(null);
  const [applicationFiles, setApplicationFiles] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    setLoading(true);
    providerService
      .getApplication(id)
      .then((data) => {
        setApplication(data);
      })
      .catch((error) => {
        console.error('Error fetching application', error);
      })
      .finally(() => {
        setLoading(false);
      });

    providerService
      .getApplicationFiles(id)
      .then((data) => {
        setApplicationFiles(data);
      })
      .catch((error) => {
        console.error('Error fetching application files', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return (
    <Spin spinning={loading}>
      <div className="space-y-8">
        {/* Family Information - Always show as first section */}
        {[PROVIDER_APPLICATION_PROGRAM_TYPES.MCCYN.id, PROVIDER_APPLICATION_PROGRAM_TYPES.ARMY_RESPITE.id].includes(
          application?.programType.id,
        ) && <FamilyInformation application={application} isEditable={isEditable} />}

        {application?.careFacility?.newOwner && (
          <Card title="Change of Ownership" noBodyPadding>
            <TabularList
              indented
              data={[
                {
                  title: 'Provider indicated that they are a new owner taking over an existing facility',
                  value: 'Yes',
                },
              ]}
            />
          </Card>
        )}

        {application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.MCCYN.id && (
          <Accreditations
            application={application}
            applicationFiles={applicationFiles}
            setApplicationFiles={setApplicationFiles}
            isEditable={isEditable}
          />
        )}

        {/* MCCYN & Army Respite */}
        {[PROVIDER_APPLICATION_PROGRAM_TYPES.MCCYN.id, PROVIDER_APPLICATION_PROGRAM_TYPES.ARMY_RESPITE.id].includes(
          application?.programType.id,
        ) && (
          <Rates
            application={application}
            applicationFiles={applicationFiles}
            setApplicationFiles={setApplicationFiles}
            isEditable={isEditable}
          />
        )}

        {/* This section is specific to MCCYN */}
        {application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.MCCYN.id && (
          <>
            <RegistrationFee application={application} isEditable={isEditable} />

            <Discounts application={application} isEditable={isEditable} />

            <IncludedCosts application={application} isEditable={isEditable} />

            <Card title="Confirmation of responses">
              <Checkbox checked={application?.agreeToDiscountTerms} disabled className="text-[15px]">
                <span className="text-red-500 mr-1">*</span>

                <span className="text-gray-700">
                  I certify that all above information is correct and all discounts that I offer are listed. I
                  understand that any changes to the discounts listed on this form must be reported to Child Care Aware
                  of America. I certify that any registration fees listed on this form are used solely to hold the spot
                  for a family and are not applied to their child care tuition, facility maintenance/supplies, or
                  activities.
                </span>
              </Checkbox>
            </Card>
          </>
        )}

        {/* These sections are unique to HCC */}
        {(application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id ||
          application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.NAVY_HCC.id) && (
          <>
            {/* Air Force HCC Accreditation & Education */}
            {application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id && (
              <AirForceHCCAccreditations
                application={application}
                applicationFiles={applicationFiles}
                setApplicationFiles={setApplicationFiles}
                isEditable={isEditable}
              />
            )}

            {/* Program Information */}
            <ProgramInfo application={application} isEditable={isEditable} />

            {/* Staff members */}
            <StaffMembers application={application} applicationFiles={applicationFiles} isEditable={isEditable} />

            {/* Residents */}
            <Residents application={application} isEditable={isEditable} />

            {/* These sections are unique to Air Force HCC */}
            {application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id && (
              <>
                {/* Insurance */}
                <Insurance
                  application={application}
                  applicationFiles={applicationFiles}
                  setApplication={setApplication}
                  setApplicationFiles={setApplicationFiles}
                  isEditable={isEditable}
                />
              </>
            )}
          </>
        )}
      </div>
    </Spin>
  );
};

export default ApplicationDetails;
